/**
 * userValidation.js — Source unique de validation des champs User/Retailer
 * 
 * ARCHITECTURE:
 * - Définitions de champs communes (type, validation, labels)
 * - Contextes d'obligation différents (inscription vs profil)
 * - Utilisé par SignupPage et ProfilePage
 * 
 * COLLECTION SOURCE: MongoDB `users`
 * MODÈLE SOURCE: backend/models.py → User / UserCreate
 */

// ═══════════════════════════════════════════════════════════
// VALIDATEURS UNITAIRES
// ═══════════════════════════════════════════════════════════

export const validators = {
  email: (value) => {
    if (!value) return { valid: false, message: 'Email requis' };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return { valid: false, message: 'Format email invalide' };
    return { valid: true, message: '' };
  },

  password: (value) => {
    if (!value) return { valid: false, message: 'Mot de passe requis' };
    if (value.length < 6) return { valid: false, message: 'Minimum 6 caractères' };
    return { valid: true, message: '' };
  },

  siret: (value) => {
    if (!value) return { valid: false, message: 'SIRET requis' };
    const clean = value.replace(/[\s-]/g, '');
    if (!/^\d{14}$/.test(clean)) return { valid: false, message: 'Le SIRET doit contenir exactement 14 chiffres' };
    return { valid: true, message: '' };
  },

  required: (value, label = 'Ce champ') => {
    if (!value || !String(value).trim()) return { valid: false, message: `${label} est obligatoire` };
    return { valid: true, message: '' };
  },

  positiveInt: (value) => {
    if (value === null || value === undefined || value === '') return { valid: true, message: '' };
    const n = parseInt(value);
    if (isNaN(n) || n < 0) return { valid: false, message: 'Doit être un nombre positif' };
    return { valid: true, message: '' };
  },

  positiveFloat: (value) => {
    if (value === null || value === undefined || value === '') return { valid: true, message: '' };
    const n = parseFloat(value);
    if (isNaN(n) || n < 0) return { valid: false, message: 'Doit être un nombre positif' };
    return { valid: true, message: '' };
  },
};

// ═══════════════════════════════════════════════════════════
// DÉFINITION DES CHAMPS — source unique
// ═══════════════════════════════════════════════════════════

export const USER_FIELDS = {
  // Auth
  email:                  { label: 'Email',                   type: 'email',    group: 'auth' },
  password:               { label: 'Mot de passe',            type: 'password', group: 'auth' },
  // Identification
  nom_magasin:            { label: 'Nom du magasin',          type: 'text',     group: 'identification' },
  prenom_utilisateur:     { label: 'Prénom',                  type: 'text',     group: 'identification' },
  nom_utilisateur:        { label: 'Nom',                     type: 'text',     group: 'identification' },
  // Coordonnées
  adresse_magasin:        { label: 'Adresse du magasin',      type: 'text',     group: 'coordonnees' },
  code_postal:            { label: 'Code postal',             type: 'text',     group: 'coordonnees' },
  ville:                  { label: 'Ville',                   type: 'text',     group: 'coordonnees' },
  telephone:              { label: 'Téléphone',               type: 'tel',      group: 'coordonnees' },
  // Magasin
  siret:                  { label: 'SIRET',                    type: 'text',     group: 'identification', validate: 'siret' },
  date_creation_magasin:  { label: 'Date de création',        type: 'date',     group: 'magasin' },
  surface_magasin:        { label: 'Surface (m²)',       type: 'number',   group: 'magasin',   validate: 'positiveInt' },
  ca_magasin:             { label: 'CA annuel (€)',      type: 'number',   group: 'magasin',   validate: 'positiveFloat' },
  nb_salaries:            { label: 'Nombre de salariés', type: 'number',   group: 'magasin',   validate: 'positiveInt' },
  zone_implantation:      { label: "Zone d'implantation",     type: 'select',   group: 'magasin',
    options: [
      { value: 'centre ville', label: 'Centre ville' },
      { value: 'périférie', label: 'Périphérie' },
      { value: 'centre commercial', label: 'Centre commercial' },
      { value: 'zone rural', label: 'Zone rurale' },
    ]
  },
  // Commerce
  site_ecommerce:         { label: 'Site e-commerce',         type: 'boolean',  group: 'commerce' },
  capacite_accueil:       { label: "Capacité d'accueil", type: 'number',   group: 'commerce',  validate: 'positiveInt' },
  jeu_libre:              { label: 'Jeu libre en magasin',  type: 'boolean',  group: 'commerce' },
  animations_magasin:     { label: 'Animations et événements', type: 'boolean', group: 'commerce' },
  // Personnel
  date_naissance:         { label: 'Date de naissance',       type: 'date',     group: 'personnel' },
  // Préférences
  newsletter:             { label: 'Newsletter',              type: 'checkbox', group: 'preferences' },
};

// ═══════════════════════════════════════════════════════════
// CONTEXTES D'OBLIGATION
// ═══════════════════════════════════════════════════════════

/** Champs obligatoires à l'inscription */
export const SIGNUP_REQUIRED = ['email', 'password', 'siret'];

/** Champs affichés à l'inscription */
export const SIGNUP_FIELDS = ['email', 'password', 'siret'];

/**
 * CHAMPS OBLIGATOIRES DU PROFIL RETAILER
 * 
 * SOURCE DE VÉRITÉ: backend check_retailer_profile_complete()
 * Cette liste DOIT correspondre exactement à la liste backend.
 * Toute modification ici doit être synchronisée avec server.py.
 */
export const PROFILE_REQUIRED = [
  'pseudo',
  'prenom_utilisateur',
  'nom_utilisateur',
  'nom_magasin',
  'raison_sociale',
  'siret',
  'adresse_magasin',
  'code_postal',
  'ville',
];

// ═══════════════════════════════════════════════════════════
// CALCUL DE COMPLÉTION DU PROFIL
// ═══════════════════════════════════════════════════════════

/**
 * Complétion = uniquement les champs obligatoires.
 * Les champs facultatifs ne comptent pas.
 * V5: Un champ obligatoire en status 'pending' n'est PAS considéré comme complété.
 */
export function computeProfileCompletion(user) {
  if (!user) return { percent: 0, filled: 0, total: PROFILE_REQUIRED.length, missing: [...PROFILE_REQUIRED], pending_count: 0 };

  let filled = 0;
  const missing = [];
  const meta = user.field_metadata || {};
  let pending_count = 0;

  for (const field of PROFILE_REQUIRED) {
    const val = user[field];
    const fieldMeta = meta[field];
    const hasValue = val !== null && val !== undefined && val !== '' && val !== 0;
    const isPending = fieldMeta && fieldMeta.status === 'pending';
    
    if (hasValue && !isPending) {
      filled++;
    } else {
      missing.push(field);
      if (hasValue && isPending) pending_count++;
    }
  }

  return {
    percent: Math.round((filled / PROFILE_REQUIRED.length) * 100),
    filled,
    total: PROFILE_REQUIRED.length,
    missing,
    pending_count,
  };
}

// ═══════════════════════════════════════════════════════════
// VALIDATION CONTEXTUELLE
// ═══════════════════════════════════════════════════════════

/**
 * Valide un jeu de données selon un contexte (signup ou profile)
 * @param {Object} data - Les données du formulaire
 * @param {'signup'|'profile'} context - Le contexte de validation
 * @returns {{ valid: boolean, errors: Object }}
 */
export function validateUserData(data, context = 'profile') {
  const errors = {};
  const requiredFields = context === 'signup' ? SIGNUP_REQUIRED : PROFILE_REQUIRED;

  for (const field of requiredFields) {
    const result = validators.required(data[field], USER_FIELDS[field]?.label || field);
    if (!result.valid) errors[field] = result.message;
  }

  // Email format
  if (data.email) {
    const r = validators.email(data.email);
    if (!r.valid) errors.email = r.message;
  }

  // Password (signup only)
  if (context === 'signup' && data.password !== undefined) {
    const r = validators.password(data.password);
    if (!r.valid) errors.password = r.message;
  }

  // SIRET validation (always validate if present)
  if (data.siret !== undefined && data.siret !== null && data.siret !== '') {
    const r = validators.siret(data.siret);
    if (!r.valid) errors.siret = r.message;
  } else if (requiredFields.includes('siret')) {
    errors.siret = 'SIRET est obligatoire';
  }

  // Field-specific validators
  for (const [field, def] of Object.entries(USER_FIELDS)) {
    if (def.validate && data[field] !== undefined && data[field] !== null && data[field] !== '') {
      const r = validators[def.validate]?.(data[field]);
      if (r && !r.valid) errors[field] = r.message;
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}
